import { Component, OnInit, signal, viewChild, inject } from '@angular/core';
import { RequestBuilderService } from '../../../../shared/services/request-builder.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { Table, TableModule } from 'primeng/table';
import { MenuModule } from 'primeng/menu';
import { CommonModule } from '@angular/common';
import { MenuItem, MessageService } from 'primeng/api';
import { getScheduledNotificationStatus, ScheduledNotification, ScheduledNotificationActionType, ScheduledNotificationStatus } from './scheduled-notification';
import { TagComponent } from '../../../../shared/components/tag/tag.component';
import { SendScheduledNotificationDialogComponent } from '../send-scheduled-notification-dialog/send-scheduled-notification-dialog.component';
import { ScheduledNotificationHandlerComponent } from './scheduled-notification-handler/scheduled-notification-handler.component';
import { FiltersComponent } from '../../../../shared/components/filters/filters.component';
import { IFilterInput } from '../../../../shared/interfaces/filter-input.interface';
import { filterInputs } from './notification-filter-inputs';
import { TimeFormatPipe } from '../../../../shared/pipes/time-format.pipe';
import { NoRecordsComponent } from '../../../../shared/components/no-records/no-records.component';

@Component({
  selector: 'app-notification-queue-dialog',
  standalone: true,
  imports: [ButtonComponent, TableModule, MenuModule, CommonModule, TagComponent, FiltersComponent, TimeFormatPipe, NoRecordsComponent],
  templateUrl: './notification-queue-dialog.component.html',
  styleUrl: './notification-queue-dialog.component.scss',
})
export class NotificationQueueDialogComponent implements OnInit {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly dialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);

  loading = false;
  notificationQueue = signal<ScheduledNotification[]>([]);
  rows = 5;
  first = 0;
  totalRecords = 0;
  rowsPerPageOptions: number[] = [5, 10, 25, 50, 100];
  columns: any[] = [];
  menuItems = signal<MenuItem[] | undefined>([]);
  inputs: IFilterInput[] = [];
  statusOptions = [
    { label: 'Scheduled', value: ScheduledNotificationStatus.Scheduled },
    { label: 'Sent', value: ScheduledNotificationStatus.Sent },
    { label: 'Failed', value: ScheduledNotificationStatus.Failed },
    { label: 'Cancelled', value: ScheduledNotificationStatus.Cancelled },
  ];
  appliedFilters: any = null;
  readonly filtersComponentRef = viewChild.required<FiltersComponent>('filtersComponent');
  ngOnInit() {
    this.getNotificationQueue();
    this.columns = [
      { field: 'Title', name: 'Message Title', type: 'text' },
      { field: 'Status', name: 'Status', type: 'object' },
    ];

    this.inputs = filterInputs;
    this.updateDropdownOptions('status', this.statusOptions);
  }

  updateDropdownOptions(controlName: string, newOptions: { label: string; value: any }[]): void {
    const filter = this.inputs.find(f => f.controlName === controlName);
    if (filter) {
      filter.options = newOptions;
    }
  }

  setMenu(e: any, selectedNotification: ScheduledNotification, menuRef: any) {
    this.menuItems.set([
      {
        label: 'View',
        command: e => {
          this.openSendScheduledNotificationDialog(selectedNotification, true);
        },
      },
      {
        label: 'Send Now',
        command: e => {
          this.openSendNotificationHandler(
            selectedNotification,
            'Send Scheduled Notification Now',
            'Send scheduled notification now will notify all contractors Immediately. are you sure you want to proceed?',
            ScheduledNotificationActionType.SendNow,
          );
        },
        styleClass: selectedNotification.Status.id === ScheduledNotificationStatus.Scheduled ? '' : 'hidden',
      },
      {
        label: 'Edit',
        command: e => {
          this.openSendScheduledNotificationDialog(selectedNotification);
        },
        styleClass: selectedNotification.Status.id === ScheduledNotificationStatus.Scheduled ? '' : 'hidden',
      },
      {
        label: 'Cancel',
        command: e => {
          this.openSendNotificationHandler(
            selectedNotification,
            'Cancel Scheduled Notification',
            'Cancel scheduled notification will cause remove it from schedule permanently. Are you sure you want to proceed?',
            ScheduledNotificationActionType.Cancel,
          );
        },
        styleClass: selectedNotification.Status.id === ScheduledNotificationStatus.Scheduled ? '' : 'hidden',
      },
      {
        label: 'Resend Now',
        command: e => {
          this.openSendNotificationHandler(selectedNotification, 'Send Scheduled Notification Now', 'Send scheduled notification now will notify all contractors Immediately. are you sure you want to proceed?', ScheduledNotificationActionType.Resend);
        },
        styleClass: selectedNotification.Status.id === ScheduledNotificationStatus.Failed ? '' : 'hidden',
      },
    ]);
    menuRef.toggle(e);
  }
  readonly table = viewChild<Table>('table');

  getNotificationQueue(filters?: any) {
    if (filters?.fromDate && filters?.toDate) {
      if (new Date(filters.fromDate) > new Date(filters.toDate)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'From Date cannot be later than To Date.',
          life: 6000,
        });
        this.filtersComponentRef().filterResults = [];
        return;
      }
    }
    this.requestBuilder
      .post('notifications/schedule-notification', {
        status: filters?.status ?? null,
        fromDate: filters?.fromDate ?? null,
        toDate: filters?.toDate ?? null,
        search: filters?.search ?? '',
      })
      .subscribe({
        next: (res: any) => {
          this.first = 0;
          this.notificationQueue.set(
            res.data.map((notification: ScheduledNotification) => {
              return { ...notification, Status: getScheduledNotificationStatus(notification.Status) };
            }),
          );
        },
        error: err => {
          console.log(err);
        },
      });
  }

  openSendScheduledNotificationDialog(selectedNotification: ScheduledNotification, viewOnly?: boolean) {
    const ref = this.dialogService.open(SendScheduledNotificationDialogComponent, {
      showHeader: false,
      width: '640px',
      modal: true,
      data: {
        selectedNotification,
        viewOnly: viewOnly ?? false,
        headerTitle: viewOnly ? 'View Notification' : 'Edit Scheduled Notification',
      },
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '768px': '90vw',
      },
    })!;

    ref.onClose.subscribe((res: any) => {
      if (res?.confirmed) {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Notification updated successfully!`,
          life: 6000,
        });
        this.getNotificationQueue(this.appliedFilters);
      }
    });
  }

  openSendNotificationHandler(selectedNotification: ScheduledNotification, header: string, confirmationText: string, actionId: number) {
    const ref = this.dialogService.open(ScheduledNotificationHandlerComponent, {
      showHeader: false,
      width: '400px',
      modal: true,
      data: {
        selectedNotification,
        header,
        confirmationText,
        actionId,
      },
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '768px': '90vw',
      },
    })!;

    ref.onClose.subscribe((res: any) => {
      if (res?.confirmed) {
        let messageDetail = '';
        switch (actionId) {
          case ScheduledNotificationActionType.Cancel:
            messageDetail = 'Notification cancelled successfully';
            break;
          case ScheduledNotificationActionType.SendNow:
            messageDetail = 'Notification send request in progress';
            break;
          case ScheduledNotificationActionType.Resend:
            messageDetail = 'Notification resend request in progress';
            break;
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: messageDetail,
          life: 6000,
        });
        this.getNotificationQueue(this.appliedFilters);
      }
    });
  }

  search(filters: any) {
    if (!filters) {
      this.appliedFilters = null;
      this.getNotificationQueue();
      return;
    }

    const mappedFilters: any = {
      search: '',
    };
    filters.filterResults.forEach((filter: any) => {
      const controlName = filter.moreInformation.controlName;
      let value = filter.value;

      mappedFilters[controlName] = value;
    });
    if (filters.search) {
      mappedFilters.search = filters.search;
    }
    this.appliedFilters = mappedFilters;
    this.getNotificationQueue(this.appliedFilters);
  }

  onPage(event: any) {
    this.first = event.first;
    this.rows = event.rows;
  }
}
